import React, { useMemo } from 'react';
import TaskDistributionSearchActions from './TaskDistributionSearchActions.jsx';
import TaskDistributionSectionHeading from './TaskDistributionSectionHeading.jsx';

const TaskDistributionTeamsStep = ({
  teamItems,
  selectedTeamIds,
  isLoadingTeamAvailability,
  searchValue,
  onSearchChange,
  onTeamsChange
}) => {
  const normalizedSearch = searchValue.trim().toLowerCase();
  const visibleTeams = useMemo(() => teamItems.filter((item) => (
    !normalizedSearch
    || `${item.label} ${item.searchText || ''}`.toLowerCase().includes(normalizedSearch)
  )), [teamItems, normalizedSearch]);
  const selectedTeamSet = useMemo(() => new Set(selectedTeamIds), [selectedTeamIds]);
  const enabledTeamCount = teamItems.filter((item) => !item.isDisabled).length;

  const toggleTeam = (teamId) => {
    if (teamItems.find((item) => item.value === teamId)?.isDisabled) {
      return;
    }

    onTeamsChange(
      selectedTeamSet.has(teamId)
        ? selectedTeamIds.filter((value) => value !== teamId)
        : [...selectedTeamIds, teamId]
    );
  };

  return (
    <div className="space-y-4">
      <TaskDistributionSectionHeading
        title="Equipos"
        description="Selecciona los equipos que participarán de la distribución."
        count={`${selectedTeamIds.length}/${enabledTeamCount}`}
      />

      {isLoadingTeamAvailability && (
        <div className="rounded-xl border border-line bg-surface-2/70 px-3 py-2 text-xs text-content-muted">
          Consultando disponibilidad para la fecha seleccionada...
        </div>
      )}

      <TaskDistributionSearchActions
        value={searchValue}
        onChange={onSearchChange}
        placeholder="Buscar equipo..."
        onSelectVisible={() => onTeamsChange(
          visibleTeams.filter((item) => !item.isDisabled).map((item) => item.value)
        )}
        onClear={() => onTeamsChange([])}
      />

      <div className="max-h-[360px] space-y-2 overflow-y-auto pr-1">
        {!visibleTeams.length && (
          <div className="rounded-xl border border-dashed border-line bg-surface-2/60 px-3 py-5 text-sm text-content-muted">
            No hay equipos activos para mostrar.
          </div>
        )}

        {visibleTeams.map((item) => {
          const isSelected = selectedTeamSet.has(item.value);
          const isDisabled = item.isDisabled;
          return (
            <label
              key={item.value}
              className={`flex items-start gap-3 rounded-xl border px-3 py-3 transition ${
                isDisabled
                  ? 'cursor-not-allowed border-line bg-surface-2/50 opacity-55 grayscale'
                  : isSelected
                    ? 'border-amber bg-amber-hover/10'
                    : 'cursor-pointer border-line bg-surface-2/70 hover:border-line'
              }`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                disabled={isDisabled}
                onChange={() => toggleTeam(item.value)}
                className="mt-0.5 h-4 w-4 rounded border-line bg-surface text-accent-text disabled:cursor-not-allowed"
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-content">{item.label}</span>
                <span className="mt-1 block truncate text-xs text-content-muted">
                  {isDisabled
                    ? (item.disabledReason
                      ? `Inhabilitado: ${item.disabledReason}`
                      : 'Inhabilitado para la fecha seleccionada')
                    : (item.team?.description?.trim() || 'Sin descripción')}
                </span>
              </span>

              {isDisabled && (
                <span className="shrink-0 rounded-full border border-line bg-surface-2 px-2 py-1 text-[9px] font-semibold uppercase tracking-wide text-content-muted">
                  Inhabilitado
                </span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default TaskDistributionTeamsStep;
